import { ComponentType } from '@angular/cdk/portal';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { ControlContainer, FormGroupDirective, NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { AddressInformationFormGroupType } from 'src/app/models/utils/forms/address-information-form';
import {
    MfaUuIdRequest,
    ModuleMFAEnum,
    ResponseMFA,
    TypeModuleMFA,
} from 'src/app/modules/two-factor/verification-code/models/two-factor-models';
import { TwoFactorComponent } from 'src/app/modules/two-factor/verification-code/two-factor.component';
import { TwoFactorService } from 'src/app/services/requests/two-factor.service';
import { ErrorHandlerService } from 'src/app/services/utils/error-handler.service';
import { regex } from 'src/app/utils/constants';
import { CustomValidators } from 'src/app/utils/custom-validators';
import Swal from 'sweetalert2';
import {
    BusinessDetailsFormGroupType,
    PersonalInformationFormGroupType,
    SecurityFormGroupType,
} from './models/form-types';
import { BusinessDetailsComponent } from './stepper/business-details/business-details.component';
import { PersonalInformationComponent } from './stepper/personal-information/personal-information.component';
import { SecurityStepComponent } from './stepper/security-step/security-step.component';

@Component({
    selector: 'app-apply-for-cargo-facility',
    templateUrl: './apply-for-cargo-facility.component.html',
    styleUrl: './apply-for-cargo-facility.component.scss',
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    standalone: false,
})
export class ApplyForCargoFacilityComponent {
    @ViewChild('personaInformtionComponent') personalInformationComponent: PersonalInformationComponent;
    @ViewChild('businessDetails') businessDetailsComponent: BusinessDetailsComponent;
    @ViewChild('securityStep') securityComponent: SecurityStepComponent;
    @ViewChild('stepper') stepper!: MatStepper;
    @ViewChild('twoFactorModal') twoFactorModal?: ComponentType<TwoFactorComponent>;
    public readonly personalInformationForm: PersonalInformationFormGroupType;
    public readonly businessDetailsForm: BusinessDetailsFormGroupType;
    public readonly securityForm: SecurityFormGroupType;
    public dynamicResponse: ResponseMFA;
    public readonly module: TypeModuleMFA = ModuleMFAEnum.FACILITY_APPLICATION_SUBMIT;
    public mfaUuId2Request: MfaUuIdRequest | undefined;

    constructor(
        private readonly fb: NonNullableFormBuilder,
        private readonly router: Router,
        private readonly twoFactorService: TwoFactorService,
        private readonly errorHandlerService: ErrorHandlerService
    ) {
        this.personalInformationForm = this.fb.group({
            firstName: [
                '',
                [
                    Validators.required,
                    Validators.minLength(3),
                    CustomValidators.preventHTMLContent(),
                    CustomValidators.noNumbersValidator(),
                ],
            ],
            lastName: [
                '',
                [
                    Validators.required,
                    Validators.minLength(3),
                    CustomValidators.preventHTMLContent(),
                    CustomValidators.noNumbersValidator(),
                ],
            ],
            email: ['', [Validators.required, Validators.pattern(regex.email)]],
            phoneForm: this.fb.group({
                code: this.fb.control('', { validators: [Validators.required] }),
                phone: this.fb.control('', {
                    validators: [Validators.required],
                }),
            }),
        });

        this.businessDetailsForm = this.fb.group({
            businessName: ['', [Validators.required]],
            sameAsMyName: [false, []],
            ein: ['', [Validators.required, Validators.pattern(regex.ein)]],
            w9File: ['', [Validators.required]],
            bankLetterFile: ['', []],
            addressForm: <AddressInformationFormGroupType>this.fb.group({
                street: ['', [Validators.required]],
                street2: ['', []],
                city: ['', [Validators.required]],
                state: [
                    '',
                    [
                        Validators.required,
                        Validators.maxLength(2),
                        Validators.minLength(2),
                        CustomValidators.uppercaseValidator(),
                    ],
                ],
                country: ['', [Validators.required]],
                zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
            }),
        });

        this.securityForm = this.fb.group({
            password: ['', [Validators.required]],
            acceptedTerms: [false, [Validators.requiredTrue]],
        });
    }

    get isStepCompleted(): boolean {
        return this.dynamicResponse?.isVerified;
    }

    handleDynamicResponseEvent(event: ResponseMFA): void {
        this.dynamicResponse = event;

        if (event?.dynamicResponseParameter?.isExistingCustomer === 'true') {
            this.securityForm.controls['password'].clearValidators();
            this.securityForm.controls['password'].updateValueAndValidity();
        }

        this.mfaUuId2Request = event.mfaUuId2Request;
    }

    submitEvent(): void {
        const dynamicParameter = {
            id: this.dynamicResponse.dynamicResponseParameter?.id,
            businessName: this.businessDetailsForm.controls['businessName'].value,
            employerIdentificationNumber: this.businessDetailsForm.controls['ein'].value,
            address: this.businessDetailsForm.controls['addressForm'].controls['street'].value,
            address2: this.businessDetailsForm.controls['addressForm'].controls['street2'].value
                ? this.businessDetailsForm.controls['addressForm'].controls['street2'].value
                : undefined,
            city: this.businessDetailsForm.controls['addressForm'].controls['city'].value,
            state: this.businessDetailsForm.controls['addressForm'].controls['state'].value,
            zip: this.businessDetailsForm.controls['addressForm'].controls['zipCode'].value,
            country: this.businessDetailsForm.controls['addressForm'].controls['country'].value,
            password:
                this.dynamicResponse?.dynamicResponseParameter?.isExistingCustomer === 'true'
                    ? undefined
                    : this.securityForm.controls['password'].value,
            w9File: this.businessDetailsForm.controls['w9File'].value,
            voidCheckFile: this.businessDetailsForm.controls['bankLetterFile'].value
                ? this.businessDetailsForm.controls['bankLetterFile'].value
                : undefined,
        };

        this.twoFactorService
            .verifyMFA({
                mfaUuId: this.mfaUuId2Request?.mfaUuId2 ?? '',
                mfaCode: this.mfaUuId2Request?.mfaCode ?? '',
                activation: this.mfaUuId2Request?.activation,
                password: this.mfaUuId2Request?.password,
                dynamicParameter: dynamicParameter,
            })
            .pipe(take(1))
            .subscribe({
                next: (response: ResponseMFA) => {
                    if (response.isVerified) {
                        this.router.navigate(['signup/payee-signup/success']);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    const errors = this.errorHandlerService.getHttpErrorResponse(error);
                    Swal.fire({
                        html: `${errors}`,
                        icon: 'error',
                        showConfirmButton: false,
                        showCancelButton: true,
                        cancelButtonText: 'Close',
                        allowOutsideClick: false,
                    });
                },
            });
    }

    cancelEvent(): void {
        const emailControl = this.personalInformationForm.controls['email'];
        if (emailControl.valid) {
            this.twoFactorService
                .resetMFA('EMAIL', emailControl.value)
                .pipe(take(1))
                .subscribe({
                    next: () => {
                        this.resetForms();
                    },
                    error: (error: HttpErrorResponse) => {
                        const errors = this.errorHandlerService.getHttpErrorResponse(error);
                        Swal.fire({
                            html: `${errors}`,
                            icon: 'error',
                            showConfirmButton: false,
                            showCancelButton: true,
                            cancelButtonText: 'Close',
                            allowOutsideClick: false,
                        });
                    },
                });
        } else {
            this.resetForms();
        }
    }

    resetForms(): void {
        // Personal Information
        this.personalInformationForm.reset();
        this.personalInformationForm.controls['phoneForm'].reset();
        this.personalInformationComponent.phoneInputComponent?.phoneForm.reset();
        this.personalInformationForm.updateValueAndValidity();
        this.personalInformationForm.markAsPristine();
        this.personalInformationForm.markAsUntouched();

        // Budiness information
        this.businessDetailsForm.reset();
        this.businessDetailsForm.controls['addressForm'].reset();
        this.businessDetailsComponent?.addressInformationComponent?.addressForm?.reset();
        this.businessDetailsComponent?.w9FileRemover?.clearFiles();
        this.businessDetailsComponent?.bankLetterFileRemover?.clearFiles();

        // Security
        this.securityForm.reset();
        this.securityComponent?.formPasswordComponent?.securityForm?.reset();
    }
}
